/**
 * Der Einstieg: Wer nicht angemeldet ist, sieht die Anmeldung. Wer angemeldet
 * ist und Bürorechte hat, sieht den Rahmen mit den Bildschirmen.
 *
 * Die Zwischenzustände sind hier genauso ausformuliert wie die guten Fälle —
 * „prüft", „kein Zugang", „gestört". Das ist Absicht: Wer im Büro vor einem
 * hängenden Bildschirm sitzt, muss lesen können, worauf gewartet wird. Ein
 * Ladekringel ohne Text ist bei einer Anmeldung die schlechteste aller
 * Antworten, weil er nicht zwischen „gleich da" und „kaputt" unterscheidet.
 */

import { Navigate, Route, Routes, useParams } from 'react-router-dom';
import { useSitzung } from './anmeldung/Sitzung';
import { AnmeldenSeite } from './anmeldung/AnmeldenSeite';
import { Rahmen } from './Rahmen';
import { MitarbeiterSeite } from './seiten/MitarbeiterSeite';
import { ZugangSeite } from './seiten/ZugangSeite';
import { FahrzeugeSeite } from './seiten/FahrzeugeSeite';
import { KundenSeite } from './seiten/KundenSeite';
import { OrteSeite } from './seiten/OrteSeite';
import { StreckenSeite } from './seiten/StreckenSeite';
import { DispositionSeite } from './seiten/DispositionSeite';
import { AbrechnungSeite } from './seiten/AbrechnungSeite';
import { AuftragSeite } from './seiten/AuftragSeite';
import { BaldSeite, BAUPLAENE } from './seiten/BaldSeite';
import { SpesenSeite } from './seiten/SpesenSeite';
import { OffenePunkteSeite } from './seiten/OffenePunkteSeite';
import { Fehlerkasten, Knopf, Ladehinweis } from './gestaltung/bausteine';

/**
 * Die Tour-Seite hängt als Ganzes an der Tour-ID (H1.3b, E-039).
 *
 * React Router baut das Element einer Route NICHT neu auf, wenn sich nur der
 * Parameter ändert — beim Sprung von Tour A zu Tour B (Verlauf, Adresszeile)
 * blieb dieselbe `AuftragSeite` stehen: Die Notizkarte hielt den Text von A,
 * und „Notiz speichern" hätte ihn an B geschrieben; ein offenes „Bearbeiten",
 * Storno- und Löschrückfrage wanderten mit, und bis zum Laden standen die
 * Daten von A unter der Adresse von B. Mit `key={id}` ist jede Tour eine
 * eigene Seite, mit eigenem Zustand von Anfang an.
 */
function AuftragNachId() {
  const { id = '' } = useParams();
  return <AuftragSeite key={id} />;
}

export function App() {
  const { stand, abmelden, nochmalPruefen } = useSitzung();

  if (stand.art === 'prueft') {
    return (
      <div className="anmelden">
        <div className="anmelden__karte">
          <Ladehinweis was="Anmeldung wird geprüft …" />
        </div>
      </div>
    );
  }

  if (stand.art === 'abgemeldet') {
    return <AnmeldenSeite />;
  }

  if (stand.art === 'gestoert') {
    return (
      <div className="anmelden">
        <div className="anmelden__karte">
          <Fehlerkasten fehler={stand.fehler} nochmal={nochmalPruefen} />
          <Knopf art="still" onClick={() => void abmelden()}>
            Abmelden
          </Knopf>
        </div>
      </div>
    );
  }

  if (stand.art === 'kein_zugang') {
    return (
      <div className="anmelden">
        <div className="anmelden__karte">
          <div className="anmelden__marke">
            <span>Haid &amp; Jerg</span>
            <h1>Kein Zugang</h1>
          </div>
          <div className="meldung meldung--warnung">
            <div>{stand.grund}</div>
          </div>
          {stand.email && <p className="meta">Angemeldet als {stand.email}</p>}
          <Knopf art="zweit" onClick={() => void abmelden()}>
            Mit einem anderen Zugang anmelden
          </Knopf>
        </div>
      </div>
    );
  }

  // Vor allem anderen: ein fremdvergebenes Passwort muss weg. Kein Weg daran
  // vorbei, keine Adresse, die daran vorbeiführt — deshalb steht diese
  // Abfrage VOR dem Router und nicht als eine Route unter vielen.
  if (stand.passwortWechseln) {
    return <ZugangSeite erzwungen />;
  }

  return (
    <Routes>
      <Route element={<Rahmen />}>
        {/* Die Startseite ist der Bildschirm, an dem der Bürotag hängt —
            nicht die Mitarbeiterliste, die man alle paar Wochen braucht. */}
        <Route index element={<Navigate to="/disposition" replace />} />
        <Route path="/mitarbeiter" element={<MitarbeiterSeite />} />
        <Route path="/zugang" element={<ZugangSeite />} />
        <Route path="/fahrzeuge" element={<FahrzeugeSeite />} />
        <Route path="/kunden" element={<KundenSeite />} />
        <Route path="/orte" element={<OrteSeite />} />
        <Route path="/strecken" element={<StreckenSeite />} />
        <Route path="/disposition" element={<DispositionSeite />} />
        <Route path="/abrechnung" element={<AbrechnungSeite />} />
        <Route path="/auftrag/:id" element={<AuftragNachId />} />
        <Route path="/zeiten" element={<SpesenSeite />} />
        <Route path="/offen" element={<OffenePunkteSeite />} />
        {/* Die alten Adressen bleiben gültig — jemand hat sie als Lesezeichen. */}
        <Route path="/belege" element={<Navigate to="/offen" replace />} />
        <Route path="/nachpflege" element={<Navigate to="/offen" replace />} />
        {Object.entries(BAUPLAENE).map(([pfad, plan]) => (
          <Route
            key={pfad}
            path={`/${pfad}`}
            element={<BaldSeite plan={plan} />}
          />
        ))}
        {/* Eine falsche Adresse ist kein Grund für eine weiße Seite. */}
        <Route path="*" element={<Navigate to="/disposition" replace />} />
      </Route>
    </Routes>
  );
}
